import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import {
  CreateMissionReferralRequest,
  CreateMissionReferralStatus,
  MissionsClient,
  SendMissionReferralEmailRequest,
  SendMissionReferralEmailStatus,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import CustomButton from '../CustomButton';
import CustomTextInput, { InputTypeValidation } from '../CustomTextInput';
import Label from '../Label';
import Popup from '../Popup';

export interface MissionReferPopupProps {
  periodMissionConfigurationId?: string;
  branchId?: string;
  missionReferralId?: string;
  referralLink?: string;
  invitedEmail?: string;
  onEmailSent?(): void;
  onClose(): void;
}

type MissionReferStep = 'intro' | 'email';
type CopyState = 'idle' | 'copied' | 'unavailable';
type EmailState = 'idle' | 'sent';

const referralEmailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const MissionReferPopup = (props: MissionReferPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const apiMissions = useApi(MissionsClient);
  const resendInvitedEmail = props.invitedEmail?.trim() ?? '';
  const isResendFlow = !!resendInvitedEmail;
  const [step, setStep] = useState<MissionReferStep>(props.missionReferralId ? 'email' : 'intro');
  const [email, setEmail] = useState(resendInvitedEmail);
  const [referralLink, setReferralLink] = useState(props.referralLink ?? '');
  const [missionReferralId, setMissionReferralId] = useState(props.missionReferralId ?? '');
  const [forceValidation, setForceValidation] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [emailState, setEmailState] = useState<EmailState>('idle');
  const [flowErrorKey, setFlowErrorKey] = useState<string>();
  const [emailErrorKey, setEmailErrorKey] = useState<string>();
  const activeMissionReferralIdRef = useRef<string | undefined>(undefined);
  const popupWidth = Math.min(350, Math.max(layout.width - 32, 280));
  const popupMaxHeight = Math.max(layout.height - 56, 260);
  const isIntroStep = step === 'intro';
  const purple = '#6350ad';
  const purpleDark = '#3b2369';
  const popupBackground = theme.isDark ? theme.background?.modal?.toString() : '#e5e3fb';
  const bodyTextColor = theme.isDark ? theme.text?.primary?.toString() : '#1f1f2d';
  const mutedTextColor = theme.isDark ? theme.text?.primary?.toString() : '#2d2d37';
  const fieldBackground = theme.isDark ? theme.background?.primaryLight?.toString() : '#ffffff';
  const titleTextColor = theme.isDark ? '#d8d0ff' : purple;
  const successTextColor = theme.isDark ? purpleDark : '#e5e3fb';
  const successBackgroundColor = theme.isDark ? '#e5e3fb' : purpleDark;
  const errorTextColor = theme.isDark ? '#ffc2d1' : '#8e183d';

  const closeColor = isIntroStep ? purpleDark : '#ffffff';

  useEffect(() => {
    if (!props.missionReferralId) {
      return;
    }

    const referralChanged = activeMissionReferralIdRef.current !== props.missionReferralId;
    activeMissionReferralIdRef.current = props.missionReferralId;
    setStep('email');
    setEmail(resendInvitedEmail);
    setReferralLink(props.referralLink ?? '');
    setMissionReferralId(props.missionReferralId);

    if (!referralChanged) {
      return;
    }

    setForceValidation(false);
    setCopyState('idle');
    setEmailState('idle');
    setFlowErrorKey(undefined);
    setEmailErrorKey(undefined);
  }, [resendInvitedEmail, props.missionReferralId, props.referralLink]);

  const createReferralMutation = useMutation({
    retry: false,
    mutationFn: () =>
      apiMissions.createMissionReferral(
        CreateMissionReferralRequest.fromJS({
          periodMissionConfigurationId: props.periodMissionConfigurationId,
          branchId: props.branchId,
        }),
        apiMissions.tokenSource?.token
      ),
  });

  const sendReferralEmailMutation = useMutation({
    retry: false,
    mutationFn: (invitedEmail: string) =>
      apiMissions.sendMissionReferralEmail(missionReferralId, SendMissionReferralEmailRequest.fromJS({ invitedEmail }), apiMissions.tokenSource?.token),
  });

  const getCreateReferralErrorKey = (status?: CreateMissionReferralStatus) => {
    if (status === CreateMissionReferralStatus.MissionUnavailable) {
      return 'refer-mission-error-unavailable';
    }

    if (status === CreateMissionReferralStatus.MonthlyReferralLimitReached) {
      return 'refer-mission-error-limit';
    }

    return 'refer-mission-error-create';
  };

  const getSendReferralEmailErrorKey = (status?: SendMissionReferralEmailStatus) => {
    if (status === SendMissionReferralEmailStatus.EmailDeliveryFailed) {
      return 'refer-mission-error-email-delivery';
    }

    return 'refer-mission-error-email';
  };

  const startReferralFlow = async () => {
    if (createReferralMutation.isPending) {
      return;
    }

    setCopyState('idle');
    setEmailState('idle');
    setFlowErrorKey(undefined);
    setEmailErrorKey(undefined);

    if (props.missionReferralId) {
      setMissionReferralId(props.missionReferralId);
      setReferralLink(props.referralLink ?? '');
      setStep('email');
      return;
    }

    if (!props.periodMissionConfigurationId || !props.branchId) {
      setFlowErrorKey('refer-mission-error-create');
      return;
    }

    try {
      const result = await createReferralMutation.mutateAsync();
      const referralCreated = result?.success !== false && !!result?.referralUrl && !!result?.missionReferralId;

      if (!referralCreated) {
        setFlowErrorKey(getCreateReferralErrorKey(result?.status));
        return;
      }

      setReferralLink(result.referralUrl ?? '');
      setMissionReferralId(result.missionReferralId ?? '');
      setStep('email');
    } catch {
      setFlowErrorKey('refer-mission-error-create');
    }
  };

  const confirmEmail = async () => {
    if (emailState === 'sent') {
      return;
    }

    const trimmedEmail = isResendFlow ? resendInvitedEmail : email.trim();

    setForceValidation(true);
    setCopyState('idle');
    setEmailState('idle');
    setEmailErrorKey(undefined);

    if (!trimmedEmail || !referralEmailRegex.test(trimmedEmail)) {
      return;
    }

    if (!missionReferralId || sendReferralEmailMutation.isPending) {
      setEmailErrorKey('refer-mission-error-email');
      return;
    }

    try {
      const result = await sendReferralEmailMutation.mutateAsync(trimmedEmail);
      const emailSent = result?.success !== false && (result?.emailSent === true || result?.status === SendMissionReferralEmailStatus.EmailSent);

      if (!emailSent) {
        setEmailErrorKey(getSendReferralEmailErrorKey(result?.status));
        return;
      }

      if (result.referralUrl) {
        setReferralLink(result.referralUrl);
      }

      setEmailState('sent');
      props.onEmailSent?.();
    } catch {
      setEmailErrorKey('refer-mission-error-email');
    }
  };

  const copyReferralLink = async () => {
    if (!referralLink) {
      setCopyState('unavailable');
      return;
    }

    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      setCopyState('unavailable');
      return;
    }

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyState('copied');
    } catch {
      setCopyState('unavailable');
    }
  };

  const renderBubble = () =>
    !isIntroStep && (
      <Image
        source={require('../../../../assets/purple-bubble.png')}
        style={{ position: 'absolute', top: 0, right: 0, width: 118, height: 103, zIndex: 0 }}
        resizeMode="contain"
      />
    );

  const renderIntroStep = () => (
    <View style={{ alignItems: 'center', paddingHorizontal: 28, paddingBottom: 24, paddingTop: 20 }}>
      <Image source={require('../../../../assets/refer-icon.png')} style={{ width: 150, height: 74 }} resizeMode="contain" />
      <Label style={{ marginTop: 10, color: titleTextColor, fontSize: 24, lineHeight: 28, fontWeight: '700', textAlign: 'center' }} numberOfLines={2}>
        {t('refer-mission-intro-title')}
      </Label>
      <Label style={{ marginTop: 4, color: bodyTextColor, fontSize: 17, lineHeight: 21, fontWeight: '700', textAlign: 'center' }} numberOfLines={3}>
        {t('refer-mission-intro-subtitle')}
      </Label>
      <Label style={{ marginTop: 24, color: bodyTextColor, fontSize: 14, lineHeight: 18, textAlign: 'center' }} numberOfLines={6}>
        {t('refer-mission-intro-body')}
      </Label>
      <Label style={{ marginTop: 8, color: bodyTextColor, fontSize: 14, lineHeight: 18, fontWeight: '700', textAlign: 'center' }} numberOfLines={5}>
        {t('refer-mission-intro-reward')}
      </Label>
      <CustomButton
        title={createReferralMutation.isPending ? `${t('loading')}...` : t('refer-mission-cta')}
        onPress={startReferralFlow}
        disabled={createReferralMutation.isPending}
        style={{ marginTop: 20, width: 220, backgroundColor: purple, borderColor: purple }}
        textStyle={{ color: '#ffffff' }}
      />
      {!!flowErrorKey && (
        <Label style={{ marginTop: 10, color: errorTextColor, fontSize: 12, lineHeight: 16, textAlign: 'center' }} numberOfLines={3}>
          {t(flowErrorKey)}
        </Label>
      )}
    </View>
  );

  const renderEmailStep = () => (
    <View style={{ paddingHorizontal: 16, paddingBottom: 24, paddingTop: 18 }}>
      <Label style={{ color: titleTextColor, fontSize: 18, lineHeight: 22, fontWeight: '700', width: popupWidth - 130 }} numberOfLines={2}>
        {t('refer-mission-form-title')}
      </Label>
      {renderReferralLinkSection()}
      <Label style={{ marginTop: 18, color: bodyTextColor, fontSize: 14, lineHeight: 18 }} numberOfLines={4}>
        {t('refer-mission-form-body')}
      </Label>
      <CustomTextInput
        value={email}
        onChangeText={(value) => {
          if (isResendFlow || emailState === 'sent') {
            return;
          }

          setEmail(value);
          setCopyState('idle');
          setEmailState('idle');
          setEmailErrorKey(undefined);
          setForceValidation(false);
        }}
        inputTypeValidation={InputTypeValidation.email}
        required
        validateOnBlur={false}
        forceValidation={forceValidation}
        placeholder={t('email')}
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        editable={!isResendFlow && emailState !== 'sent'}
        containerStyle={{ marginTop: 12 }}
        style={{ backgroundColor: fieldBackground, borderColor: fieldBackground, minHeight: 44 }}
      />
      {emailState !== 'sent' && (
        <CustomButton
          title={sendReferralEmailMutation.isPending ? `${t('loading')}...` : t('refer-mission-send-email')}
          onPress={confirmEmail}
          disabled={sendReferralEmailMutation.isPending}
          style={{ marginTop: 20, alignSelf: 'center', width: 150, backgroundColor: purple, borderColor: purple }}
          textStyle={{ color: '#ffffff' }}
        />
      )}
      {!!emailErrorKey && (
        <Label style={{ marginTop: 8, color: errorTextColor, fontSize: 12, lineHeight: 16, textAlign: 'center' }} numberOfLines={3}>
          {t(emailErrorKey)}
        </Label>
      )}
      {emailState === 'sent' && (
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{
            marginTop: 20,
            minHeight: 44,
            borderRadius: 6,
            backgroundColor: successBackgroundColor,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <FontAwesomeIcon icon={faCheck} size={16} color={successTextColor} />
          <Label style={{ marginLeft: 8, color: successTextColor, fontSize: 13, lineHeight: 18, fontWeight: '700', textAlign: 'center' }} numberOfLines={2}>
            {t('refer-mission-email-success')}
          </Label>
        </View>
      )}
    </View>
  );

  const renderReferralLinkSection = () => (
    <View style={{ marginTop: 18 }}>
      <Label style={{ color: bodyTextColor, fontSize: 14, lineHeight: 18 }} numberOfLines={4}>
        {referralLink ? t('refer-mission-link-body') : t('reference-link-unavailable')}
      </Label>
      {!!referralLink && (
        <View
          style={{
            marginTop: 12,
            minHeight: 42,
            width: '100%',
            borderRadius: 6,
            backgroundColor: fieldBackground,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: 10,
            paddingRight: 4,
            overflow: 'hidden',
          }}
        >
          <View style={{ flex: 1, flexShrink: 1, minWidth: 0, overflow: 'hidden', marginRight: 4 }}>
            <Text
              selectable
              numberOfLines={2}
              ellipsizeMode="tail"
              style={[
                {
                  color: purple,
                  fontSize: 13,
                  lineHeight: 17,
                },
                Platform.OS === 'web' ? ({ wordBreak: 'break-all', overflowWrap: 'anywhere' } as any) : undefined,
              ]}
            >
              {referralLink}
            </Text>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('refer-mission-copy-link')}
            onPress={copyReferralLink}
            hitSlop={8}
            style={{ width: 34, height: 34, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}
          >
            <FontAwesomeIcon icon={copyState === 'copied' ? faCheck : faClipboard} size={16} color={purple} />
          </Pressable>
        </View>
      )}
      {copyState !== 'idle' && (
        <Label style={{ marginTop: 8, color: copyState === 'copied' ? purple : mutedTextColor, fontSize: 12, lineHeight: 16 }} numberOfLines={2}>
          {copyState === 'copied' ? t('refer-mission-copy-success') : t('refer-mission-copy-unavailable')}
        </Label>
      )}
    </View>
  );

  return (
    <Popup scrollable={false} suspendDrawer={false} backgroundColor={popupBackground} borderRadius={8}>
      <View style={{ width: popupWidth, maxHeight: popupMaxHeight, backgroundColor: popupBackground, overflow: 'hidden' }}>
        {renderBubble()}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('close')}
          onPress={props.onClose}
          hitSlop={12}
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, width: 28, height: 28, alignItems: 'center', justifyContent: 'center' }}
        >
          <FontAwesomeIcon icon={faXmark} size={22} color={closeColor} />
        </Pressable>
        <ScrollView
          style={{ maxHeight: popupMaxHeight }}
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
          bounces={false}
          alwaysBounceVertical={false}
          alwaysBounceHorizontal={false}
          overScrollMode="never"
        >
          <View style={{ position: 'relative', zIndex: 1 }}>
            {step === 'intro' && renderIntroStep()}
            {step === 'email' && renderEmailStep()}
          </View>
        </ScrollView>
      </View>
    </Popup>
  );
};

export default MissionReferPopup;
